import {
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
  useCombobox,
} from "downshift";
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { listFilter } from "../../util/filter";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import ChevronUpIcon from "../icons/ChevronUpIcon";
import { useFloating, flip, autoUpdate } from "@floating-ui/react";
import { createPortal } from "react-dom";

export type SuperDropdownItem<
  T,
  K extends string | number | symbol = "id",
> = Record<K, string> & T;

export default function SuperDropdown<T, K extends keyof T>(props: {
  items: SuperDropdownItem<T, K>[] | undefined;
  itemsIdKey: K;
  itemToElement: (item: T) => ReactNode;
  itemToString: (item: T | null) => string;
  onSelectedItemEvent: (
    event: UseComboboxStateChange<SuperDropdownItem<T, K>>,
  ) => void;
  asDropdown?: boolean;
  classNameContainer?: string;
  classNameInput?: string;
  classNameListbox?: string;
  clearInputAfterSelection?: boolean;
  closedIcon?: ReactNode;
  defaultHighlightedIndex?: number;
  disabled?: boolean;
  filterKeys?: Array<keyof T>;
  initialInputValue?: string;
  initialSelectedItem?: SuperDropdownItem<T, K> | null | undefined;
  inputRef?: React.RefObject<HTMLInputElement>;
  onStateChange?: (
    changes: UseComboboxStateChange<SuperDropdownItem<T, K>>,
  ) => void;
  openIcon?: ReactNode;
  placeholder?: string;
  stateReducer?: (
    state: UseComboboxState<SuperDropdownItem<T, K>>,
    actionAndChanges: UseComboboxStateChangeOptions<SuperDropdownItem<T, K>>,
  ) => Partial<UseComboboxState<SuperDropdownItem<T, K>>>;
  onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
  focusInputOnMount?: boolean;
}) {
  const {
    items,
    itemsIdKey,
    itemToElement,
    itemToString,
    onSelectedItemEvent,
    asDropdown,
    classNameContainer,
    classNameInput,
    classNameListbox,
    clearInputAfterSelection,
    closedIcon,
    defaultHighlightedIndex,
    disabled,
    filterKeys,
    initialInputValue,
    initialSelectedItem,
    onStateChange,
    openIcon,
    placeholder,
    stateReducer,
    focusInputOnMount,
  } = props;

  // state
  const [_items, setItems] = useState(items ? items : []);

  // effects
  useEffect(() => {
    if (items) {
      setItems(items);
    }
  }, [items]);

  // functions
  function onInputValueChange(
    changes: UseComboboxStateChange<SuperDropdownItem<T, K>>,
  ) {
    if (filterKeys && items) {
      const filteredItems = items.filter((item) =>
        listFilter(item, filterKeys, changes.inputValue ?? ""),
      );
      setItems(filteredItems);
    }
  }

  function onSelectedItemEventWithClear(
    changes: UseComboboxStateChange<SuperDropdownItem<T, K>>,
  ) {
    if (changes.selectedItem) {
      onSelectedItemEvent(changes);
      selectItem(null);
    }
  }

  const {
    getInputProps,
    getItemProps,
    getMenuProps,
    getToggleButtonProps,
    highlightedIndex,
    isOpen,
    selectItem,
  } = useCombobox({
    items: _items,
    itemToString: itemToString,
    initialInputValue: initialInputValue,
    initialSelectedItem: initialSelectedItem,
    defaultHighlightedIndex: defaultHighlightedIndex,
    onInputValueChange: filterKeys ? onInputValueChange : undefined,
    onSelectedItemChange: clearInputAfterSelection
      ? onSelectedItemEventWithClear
      : onSelectedItemEvent,
    onStateChange: onStateChange,
    stateReducer: stateReducer
      ? stateReducer
      : (state, actionAndChanges) => {
          return { ...state, ...actionAndChanges.changes };
        },
  });

  useLayoutEffect(() => {
    if (isOpen) {
      document.body.style.pointerEvents = "none";
    } else {
      document.body.style.pointerEvents = "auto";
    }

    return () => {
      document.body.style.pointerEvents = "auto";
    };
  }, [isOpen]);

  const { floatingStyles, refs } = useFloating<HTMLDivElement>({
    middleware: [flip()],
    whileElementsMounted: autoUpdate,
  });
  const onBlur = props.onBlur ? { onBlur: props.onBlur } : {};
  const localInputRef = useRef<HTMLInputElement>(null);
  const inputRef = props.inputRef ?? localInputRef;
  useEffect(() => {
    if (focusInputOnMount) inputRef.current?.focus();
  }, [focusInputOnMount, inputRef]);
  return (
    <div
      className={`c-dropdown-super-wrapper${
        classNameContainer ? ` ${classNameContainer}` : ``
      }`}
    >
      <div
        className={`c-dropdown-super${
          classNameInput ? ` ${classNameInput}` : ``
        }`}
        ref={refs.setReference}
      >
        <input
          className="c-dropdown-super-input"
          disabled={disabled}
          placeholder={placeholder ? placeholder : "Søg"}
          readOnly={asDropdown !== undefined ? asDropdown : !filterKeys?.length}
          {...getInputProps({ ref: inputRef, ...onBlur })}
        />
        <button
          aria-label="toggle menu"
          className=""
          type="button"
          {...getToggleButtonProps()}
          disabled={disabled}
        >
          {isOpen
            ? (openIcon ?? <ChevronUpIcon />)
            : (closedIcon ?? <ChevronDownIcon />)}
        </button>
      </div>
      {createPortal(
        <ul
          className={`c-dropdown-super-listbox${!isOpen ? " d-none" : ""}${
            classNameListbox ? ` ${classNameListbox}` : ``
          }${_items.length > 0 ? "" : " d-none"}`}
          style={{
            ...{ display: isOpen ? "block" : "none" },
            ...{ pointerEvents: isOpen ? "auto" : "none" },
            ...{
              width: (refs.reference.current as HTMLDivElement)?.offsetWidth,
            },
            ...floatingStyles,
          }}
          {...getMenuProps({
            ref: refs.setFloating,
          })}
        >
          {isOpen &&
            _items.map((item, index) => (
              <li
                className={`c-dropdown-super-listbox-item${
                  highlightedIndex === index ? " highlight" : ""
                }`}
                key={`${item[itemsIdKey]}${index}`}
                {...getItemProps({ item, index })}
              >
                {itemToElement(item)}
              </li>
            ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}
